/**
 * O ponto ÚNICO de data do front.
 *
 * Transporte é UTC. Exibição é America/Sao_Paulo. Se cada tela resolvesse
 * isso do seu jeito, o relógio de SLA erraria por três horas e o bug só
 * apareceria em produção — na máquina do dev, que já está em -03, tudo
 * pareceria certo.
 *
 * Sem biblioteca de data: `Intl` cobre tudo que precisamos e não adiciona
 * 70 KB ao bundle nem uma dependência a auditar.
 */

/** Fuso de EXIBIÇÃO. Transporte continua sendo UTC, sempre. */
export const FUSO_DE_EXIBICAO = 'America/Sao_Paulo';

const LOCALIDADE = 'pt-BR';

/**
 * Instante no tempo. É um `Date`, mas com nome que lembra que ele não tem
 * fuso — fuso é decisão de exibição.
 */
export type Instante = Date;

export class DataInvalida extends Error {
  constructor(
    readonly valorRecebido: string,
    motivo: string,
  ) {
    super(`Data inválida (${motivo}): ${JSON.stringify(valorRecebido)}`);
    this.name = 'DataInvalida';
  }
}

/**
 * ISO 8601 com offset explícito: `Z` ou `±hh:mm`.
 *
 * Texto SEM offset é recusado de propósito. `new Date('2026-09-14T10:00:00')`
 * é interpretado como hora LOCAL pelo runtime — é exatamente assim que o
 * erro de três horas entra, silencioso, vindo de um backend que esqueceu o
 * `Z`. Melhor estourar na fronteira do que calcular SLA errado.
 */
const ISO_COM_OFFSET = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(Z|[+-]\d{2}:?\d{2})$/;

function diasNoMes(ano: number, mes: number): number {
  const bissexto = (ano % 4 === 0 && ano % 100 !== 0) || ano % 400 === 0;
  return [31, bissexto ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][mes - 1] ?? 0;
}

/**
 * O calendário é conferido campo a campo, e não por `new Date(...)`.
 *
 * Motivo concreto: `new Date('2026-02-30T10:00:00Z')` NÃO devolve data
 * inválida — devolve 2 de março, silenciosamente. Um backend com um bug de
 * fuso entregaria 30 de fevereiro e o front exibiria uma data plausível,
 * dois dias adiante, sem ninguém notar. Foi um teste que descobriu isto.
 */
function calendarioValido(
  ano: number,
  mes: number,
  dia: number,
  hora: number,
  minuto: number,
  segundo: number,
): boolean {
  if (mes < 1 || mes > 12) return false;
  if (dia < 1 || dia > diasNoMes(ano, mes)) return false;
  if (hora > 23 || minuto > 59 || segundo > 59) return false;
  return true;
}

/**
 * Converte o texto que veio da API em `Instante`.
 * Lança `DataInvalida` — nunca devolve `Invalid Date` nem `null` silencioso.
 */
export function parseInstante(texto: string): Instante {
  if (texto.trim() === '') {
    throw new DataInvalida(texto, 'vazio');
  }
  const partes = ISO_COM_OFFSET.exec(texto.trim());
  if (partes === null) {
    throw new DataInvalida(texto, 'esperado ISO 8601 com offset (Z ou ±hh:mm)');
  }

  const [ano, mes, dia, hora, minuto] = partes.slice(1, 6).map(Number) as [number, number, number, number, number];
  const segundo = Number(partes[6] ?? '0');

  if (!calendarioValido(ano, mes, dia, hora, minuto, segundo)) {
    throw new DataInvalida(texto, 'não existe nesse calendário');
  }

  const instante = new Date(texto);
  if (Number.isNaN(instante.getTime())) {
    throw new DataInvalida(texto, 'não representa um instante real');
  }
  return instante;
}

/** Igual a `parseInstante`, mas devolve `null` em vez de lançar. */
export function parseInstanteOuNulo(texto: string | null | undefined): Instante | null {
  if (texto == null) return null;
  try {
    return parseInstante(texto);
  } catch {
    return null;
  }
}

function formatar(instante: Instante, opcoes: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat(LOCALIDADE, { timeZone: FUSO_DE_EXIBICAO, ...opcoes }).format(instante);
}

/** `14/09/2026` */
export function formatarData(instante: Instante): string {
  return formatar(instante, { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** `07:30` */
export function formatarHora(instante: Instante): string {
  return formatar(instante, { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** A competência do instante, `2026-10`, no fuso de exibição: às 22h de 31/10 em São Paulo ainda é outubro. */
export function competenciaDe(instante: Instante = new Date()): string {
  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: FUSO_DE_EXIBICAO,
    year: 'numeric',
    month: '2-digit',
  }).formatToParts(instante);
  const parte = (tipo: string): string => partes.find((p) => p.type === tipo)?.value ?? '';
  return `${parte('year')}-${parte('month')}`;
}

/** `14/09/2026 07:30` */
export function formatarDataHora(instante: Instante): string {
  return `${formatarData(instante)} ${formatarHora(instante)}`;
}

const UNIDADES: readonly (readonly [Intl.RelativeTimeFormatUnit, number])[] = [
  ['year', 365 * 24 * 60 * 60 * 1000],
  ['month', 30 * 24 * 60 * 60 * 1000],
  ['day', 24 * 60 * 60 * 1000],
  ['hour', 60 * 60 * 1000],
  ['minute', 60 * 1000],
];

/** `há 2 horas`, `em 3 dias`. `agora` para menos de um minuto. */
export function formatarRelativo(instante: Instante, referencia: Instante = new Date()): string {
  const delta = instante.getTime() - referencia.getTime();
  const formatador = new Intl.RelativeTimeFormat(LOCALIDADE, { numeric: 'auto' });

  for (const [unidade, milissegundos] of UNIDADES) {
    if (Math.abs(delta) >= milissegundos) {
      return formatador.format(Math.round(delta / milissegundos), unidade);
    }
  }
  return 'agora';
}

/**
 * Minutos restantes até o prazo. Negativo quando já estourou.
 * É o número que a classificação de SLA consome.
 */
export function minutosAte(prazo: Instante, referencia: Instante = new Date()): number {
  return Math.round((prazo.getTime() - referencia.getTime()) / 60_000);
}

/** ISO em UTC — o formato de TRANSPORTE. Nunca exiba isto para a pessoa. */
export function paraTransporte(instante: Instante): string {
  return instante.toISOString();
}

/** `2026-10-01` ou `2026-10` → `10/2026`. Texto fora desse formato volta como veio. */
export function formatarCompetencia(texto: string): string {
  const achado = /^(\d{4})-(\d{2})(?:-\d{2})?$/.exec(texto);
  return achado === null ? texto : `${achado[2] ?? ''}/${achado[1] ?? ''}`;
}
